/**
 * ground_keepout.ts — THE GROUND KEEP-OUT: nothing that grows on the ground stands inside a building
 * (2026-10-02, the owner: "grass was rendering inside the interiors of the buildings").
 *
 * WHY. The grass layer, the chunk ground cover and the flora worker kept off settlement PADS
 * (`settlementClearance`), player deeds and guild halls (`structureKeepOuts` discs) — and nothing else.
 * Measured over the map bake (every building the assembly places): landmarks and monuments on every tier
 * (93–100 % of them, the capital's civic core included) and hamlet buildings (97.7 %) left grass-eligible
 * ground INSIDE their footprint, because none of them stands on a pad. Walk into one and the grass came
 * up through the floor.
 *
 * WHAT IS KEPT OUT — two live registries, so the rule is what STANDS, at the size it stands:
 *  · every registered INTERIOR (`enterables`: settlements, deeds, the Hive, presets, markers) — its whole
 *    measured box, because the interior's floor is built from the box, which can be wider than the
 *    building's base;
 *  · every standing SOLID (`structure_solids`: each building's ladder bands, landmarks, deeds, guild halls,
 *    town walls and towers, mills and wells) whose band reaches the GROUND there — an upper band (a spire,
 *    an overhang) does not clear the grass under it.
 *
 * CHANGES. A building usually assembles AFTER its chunk's ground cover scattered and its grass filled,
 * so the module keeps a change log (one rect per added or removed shape): the grass field refills only
 * those tiles, the chunk scatter removes the instances that now stand inside, and the flora worker
 * re-plants that chunk. Pure — no three.js.
 */
import { structureSolidsNear, structureSolidsVersion, structureSolidsChangesSince } from './structure_solids.js';
import { enterables } from './building_interiors.js';

/** An oriented keep-out rectangle (three's rotation.y = yaw: local x → (c, −s), local z → (s, c)). */
export interface KeepShape { x: number; z: number; hx: number; hz: number; c: number; s: number; base: number; top: number }
export interface KeepChange { x0: number; z0: number; x1: number; z1: number; removed: boolean }

/** A solid band counts as standing on the ground when its base is within this of the ground height. */
export const KEEPOUT_GROUND_TOL_M = 1.5;
/** Growth margin past a standing solid's outline. 0.5 m: the grass field interpolates between texel centres 0.5 m
 *  apart, so a diagonal tap reaches 0.71 m — a smaller margin let a trace of grass back inside a turned wall. */
export const KEEPOUT_SOLID_PAD_M = 0.5;
/** Margin past an interior's box (the shell's walls stand on the box's edge). */
export const KEEPOUT_INTERIOR_PAD_M = 0.5;
/** How often the interior registry is re-read for changes (ms). */
const ENTER_SYNC_MS = 250;
const LOG_MAX = 16384;
const BIN_M = 64;

interface GKState {
  version: number;
  log: Array<KeepChange & { v: number }>;
  solidsSeen: number;
  enterSig: number;
  enterSyncAt: number;
  ent: Map<string, KeepShape>;
  bins: Map<string, KeepShape[]>;
  off?: boolean;
  offAt?: number;
}
// globalThis-backed (the instance-split law, building_interiors' precedent): two module instances must share one log.
const GG = globalThis as unknown as { __atlasGroundKeepOut?: GKState };
const st: GKState = (GG.__atlasGroundKeepOut ??= { version: 0, log: [], solidsSeen: 0, enterSig: NaN, enterSyncAt: -1e12, ent: new Map(), bins: new Map() });

function note(x0: number, z0: number, x1: number, z1: number, removed: boolean): void {
  st.version++;
  st.log.push({ v: st.version, x0, z0, x1, z1, removed });
  if (st.log.length > LOG_MAX) st.log.splice(0, st.log.length - LOG_MAX);
}

function shapeAabb(k: KeepShape): [number, number, number, number] {
  const r = Math.hypot(k.hx, k.hz);
  return [k.x - r, k.z - r, k.x + r, k.z + r];
}

function enterShape(b: { x: number; z: number; yaw: number; w: number; d: number }): KeepShape | null {
  if (![b.x, b.z, b.yaw, b.w, b.d].every(Number.isFinite) || b.w <= 0 || b.d <= 0) return null;
  return { x: b.x, z: b.z, hx: b.w / 2 + KEEPOUT_INTERIOR_PAD_M, hz: b.d / 2 + KEEPOUT_INTERIOR_PAD_M, c: Math.cos(b.yaw), s: Math.sin(b.yaw), base: -Infinity, top: Infinity };
}

/** Re-read the interior registry when its signature moved: log every shape added, moved or removed. */
function syncEnterables(now: number): void {
  if (now - st.enterSyncAt < ENTER_SYNC_MS) return;
  st.enterSyncAt = now;
  let sig = enterables.size;
  for (const b of enterables.values()) sig += b.x * 1.000_001 + b.z * 3.000_003 + b.w * 7 + b.d * 11 + b.yaw * 13;
  if (sig === st.enterSig) return;
  st.enterSig = sig;
  const next = new Map<string, KeepShape>();
  for (const [id, b] of enterables) { const k = enterShape(b); if (k) next.set(id, k); }
  for (const [id, old] of st.ent) {
    const now2 = next.get(id);
    if (now2 && now2.x === old.x && now2.z === old.z && now2.hx === old.hx && now2.hz === old.hz && now2.c === old.c) continue;
    const [x0, z0, x1, z1] = shapeAabb(old); note(x0, z0, x1, z1, true);
  }
  for (const [id, k] of next) {
    const old = st.ent.get(id);
    if (old && old.x === k.x && old.z === k.z && old.hx === k.hx && old.hz === k.hz && old.c === k.c) continue;
    const [x0, z0, x1, z1] = shapeAabb(k); note(x0, z0, x1, z1, false);
  }
  st.ent = next;
  st.bins = new Map();
  for (const k of next.values()) {
    const [x0, z0, x1, z1] = shapeAabb(k);
    for (let bz = Math.floor(z0 / BIN_M); bz <= Math.floor(z1 / BIN_M); bz++) for (let bx = Math.floor(x0 / BIN_M); bx <= Math.floor(x1 / BIN_M); bx++) {
      const key = `${bx},${bz}`; const a = st.bins.get(key); if (a) a.push(k); else st.bins.set(key, [k]);
    }
  }
}

/** Fold the solids registry's changes into the log. */
function syncSolids(): void {
  const v = structureSolidsVersion();
  if (v === st.solidsSeen) return;
  const ch = structureSolidsChangesSince(st.solidsSeen);
  st.solidsSeen = v;
  if (ch === null) { note(-1e9, -1e9, 1e9, 1e9, true); return; } // the solids log no longer reaches: everything may have moved
  for (const c of ch) note(c.x0 - KEEPOUT_SOLID_PAD_M, c.z0 - KEEPOUT_SOLID_PAD_M, c.x1 + KEEPOUT_SOLID_PAD_M, c.z1 + KEEPOUT_SOLID_PAD_M, c.removed);
}

/** The keep-out's version (re-reads both registries first; the interiors at most every 250 ms). */
export function groundKeepOutVersion(now: number = Date.now()): number {
  syncSolids();
  syncEnterables(now);
  return st.version;
}

/** Every change after version `v`, or null when the log no longer reaches back that far (treat as "anything may have changed"). */
export function groundKeepOutChangesSince(v: number): KeepChange[] | null {
  if (v >= st.version) return [];
  if (st.log.length === 0 || st.log[0].v > v + 1) return null;
  const out: KeepChange[] = [];
  for (const e of st.log) if (e.v > v) out.push({ x0: e.x0, z0: e.z0, x1: e.x1, z1: e.z1, removed: e.removed });
  return out;
}

/** THE LEVER: localStorage `atlas:ground-keepout = 'off'` gives today's rule back (pads, deeds and guild halls only). Read once a second. */
export function groundKeepOutOff(): boolean {
  const now = Date.now();
  if (st.offAt === undefined || now - st.offAt > 1000) {
    st.offAt = now;
    try { st.off = (globalThis as { localStorage?: { getItem(k: string): string | null } }).localStorage?.getItem('atlas:ground-keepout') === 'off'; } catch { st.off = false; }
  }
  return st.off === true;
}

/** Every keep-out shape that can reach the rect [x0, x1] × [z0, z1] (none while the lever is off). */
export function groundKeepOutsNear(x0: number, z0: number, x1: number, z1: number, out: KeepShape[] = []): KeepShape[] {
  out.length = 0;
  if (groundKeepOutOff()) return out;
  const cx = (x0 + x1) / 2, cz = (z0 + z1) / 2, r = Math.hypot(x1 - x0, z1 - z0) / 2 + KEEPOUT_SOLID_PAD_M;
  for (const s of structureSolidsNear(cx, cz, r)) {
    out.push({ x: s.x, z: s.z, hx: s.hx + KEEPOUT_SOLID_PAD_M, hz: s.hz + KEEPOUT_SOLID_PAD_M, c: Math.cos(s.yaw), s: Math.sin(s.yaw), base: s.base, top: s.top });
  }
  const seen = new Set<KeepShape>();
  for (let bz = Math.floor(z0 / BIN_M); bz <= Math.floor(z1 / BIN_M); bz++) for (let bx = Math.floor(x0 / BIN_M); bx <= Math.floor(x1 / BIN_M); bx++) {
    const a = st.bins.get(`${bx},${bz}`); if (!a) continue;
    for (const k of a) {
      if (seen.has(k)) continue; seen.add(k);
      const [ax0, az0, ax1, az1] = shapeAabb(k);
      if (ax1 < x0 || ax0 > x1 || az1 < z0 || az0 > z1) continue;
      out.push(k);
    }
  }
  return out;
}

/** Is (x, z) — on ground at height `groundY` — inside any of `list`? A solid only counts where its band stands on that ground. */
export function insideGroundKeepOut(list: readonly KeepShape[], x: number, z: number, groundY: number): boolean {
  for (const k of list) {
    if (k.base > groundY + KEEPOUT_GROUND_TOL_M || k.top < groundY - 0.5) continue;
    const dx = x - k.x, dz = z - k.z;
    const lx = dx * k.c - dz * k.s, lz = dx * k.s + dz * k.c; // the inverse of three's rotation.y
    if (Math.abs(lx) <= k.hx && Math.abs(lz) <= k.hz) return true;
  }
  return false;
}

/** Test-only: forget the interior snapshot and the log (harness fixtures). */
export function __resetGroundKeepOut(): void {
  st.version = 0; st.log.length = 0; st.solidsSeen = structureSolidsVersion(); st.enterSig = NaN; st.enterSyncAt = -1e12; st.ent = new Map(); st.bins = new Map();
}
